import {
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  Search,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  type AuditAction,
  type AuditEntityType,
  type AuditLogEntry,
} from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { QueryError } from "@/components/query-error";
import { useAuditInfiniteQuery } from "@/features/audit/api";
import { ISTANBUL_TZ, useFormat } from "@/lib/format";
import { useT } from "@/lib/i18n";

const KNOWN_ACTIONS = new Set<string>(AUDIT_ACTIONS);
const KNOWN_ENTITY_TYPES = new Set<string>(AUDIT_ENTITY_TYPES);

const AUDIT_DATE_TIME: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  timeZone: ISTANBUL_TZ,
};

/**
 * Aktivite (audit) sayfası — Owner/Manager için. Üst row'lar (mutation point)
 * backend'de `IAuditLogger.Log(...)` ile yazılır; bu sayfa salt-okunur listeleme.
 */
export function AuditPage() {
  return (
    <DashboardPage>
      <AuditView />
    </DashboardPage>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function AuditView() {
  const t = useT("audit");
  const tCommon = useT("common");
  const tAction = useT("auditAction");
  const tEntity = useT("auditEntityType");
  const fmt = useFormat();
  const [entityType, setEntityType] = useState<AuditEntityType | "">("");
  const [action, setAction] = useState<AuditAction | "">("");
  const [actorEmail, setActorEmail] = useState("");
  const debouncedActor = useDebouncedValue(actorEmail.trim(), 350);

  // Backend `actorMemberId` bekliyor; frontend "email arama" UX'i daha doğal.
  // MVP'de email yerine sadece member id girilirse filtre çalışır; isim/email
  // search'ini gelecek iterasyonda backend'de email LIKE ekleyince entegre ederiz.
  const filters = useMemo(
    () => ({
      entityType: entityType || undefined,
      action: action || undefined,
      // Şu an actor filtresi sadece UI'de görsel — backend MemberId bekliyor.
      // Bilinçli yorum: search box ileride email LIKE eklenince aktif olacak.
    }),
    [entityType, action],
  );

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
    isRefetching,
  } = useAuditInfiniteQuery(filters);

  const items = useMemo(
    () => data?.pages.flatMap((p) => p?.items ?? []) ?? [],
    [data],
  );
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  // Email filtresi client-side (MVP) — backend henüz LIKE'la aramıyor.
  const filtered = useMemo(() => {
    if (!debouncedActor) return items;
    const q = debouncedActor.toLowerCase();
    return items.filter((it) => it.actorEmail?.toLowerCase().includes(q));
  }, [items, debouncedActor]);

  const isEmpty = !isLoading && filtered.length === 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("title")}</h1>
        <p className="text-sm text-muted-foreground">{t("description")}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="relative">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={actorEmail}
            onChange={(e) => setActorEmail(e.target.value)}
            placeholder={t("actorPlaceholder")}
            aria-label={t("actorPlaceholder")}
            className="ps-9 pe-9"
          />
          {actorEmail && (
            <button
              type="button"
              onClick={() => setActorEmail("")}
              className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
              aria-label={t("clear")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <select
          value={entityType}
          onChange={(e) => setEntityType(e.target.value as AuditEntityType | "")}
          className="h-10 rounded-md border bg-background px-3 text-sm"
          aria-label={t("allEntityTypes")}
        >
          <option value="">{t("allEntityTypes")}</option>
          {AUDIT_ENTITY_TYPES.map((type) => (
            <option key={type} value={type}>
              {tEntity(type)}
            </option>
          ))}
        </select>

        <select
          value={action}
          onChange={(e) => setAction(e.target.value as AuditAction | "")}
          className="h-10 rounded-md border bg-background px-3 text-sm"
          aria-label={t("allActions")}
        >
          <option value="">{t("allActions")}</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {tAction(a)}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : error ? (
        <QueryError
          message={t("loadError")}
          onRetry={() => refetch()}
          retrying={isRefetching}
        />
      ) : isEmpty ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
            <History className="h-8 w-8" />
            <div>
              <p className="font-medium text-foreground">{t("emptyTitle")}</p>
              <p className="mt-1 text-sm">{t("emptyHint")}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">
            {t("showing", {
              shown: fmt.number(filtered.length),
              total: fmt.number(totalCount),
            })}
          </div>

          <Card>
            <CardContent className="p-0">
              <ul className="divide-y">
                {filtered.map((row) => (
                  <AuditRow key={row.id} row={row} />
                ))}
              </ul>
            </CardContent>
          </Card>

          {hasNextPage ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {tCommon("loading")}
                  </>
                ) : (
                  t("loadMore")
                )}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function AuditRow({ row }: { row: AuditLogEntry }) {
  const t = useT("audit");
  const tAction = useT("auditAction");
  const tEntity = useT("auditEntityType");
  const fmt = useFormat();
  const [open, setOpen] = useState(false);
  const actionLabel = KNOWN_ACTIONS.has(row.action) ? tAction(row.action) : row.action;
  const entityLabel = KNOWN_ENTITY_TYPES.has(row.entityType)
    ? tEntity(row.entityType)
    : row.entityType;
  const entityHref = buildEntityHref(row);
  const hasDiff = !!row.diff;

  return (
    <li className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:gap-4">
      <div className="min-w-[140px] text-xs text-muted-foreground tabular-nums">
        {fmt.dateTime(row.createdAt, AUDIT_DATE_TIME)}
      </div>

      <div className="flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="outline">{entityLabel}</Badge>
          <span className="font-medium text-foreground">{actionLabel}</span>
          {entityHref ? (
            <Link
              to={entityHref}
              className="font-mono text-xs text-muted-foreground hover:text-foreground hover:underline"
              title={row.entityId}
            >
              #{shortId(row.entityId)}
            </Link>
          ) : (
            <span
              className="font-mono text-xs text-muted-foreground"
              title={row.entityId}
            >
              #{shortId(row.entityId)}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>{row.actorEmail ?? t("deletedMember")}</span>
          {row.ipAddress && <span>· {t("ip", { value: row.ipAddress })}</span>}
        </div>

        {hasDiff && (
          <div>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              {open ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
              )}
              {t("details")}
            </button>
            {open && <DiffPanel diffJson={row.diff!} userAgent={row.userAgent} />}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Diff JSON'ı pretty-print eder. Backend convention:
 * <code>{"field": {"oldValue": ..., "newValue": ...}}</code> — bu pattern'ı
 * tablo gibi gösterir. Diğer (insert/delete) kayıtlarda raw JSON.
 */
function DiffPanel({
  diffJson,
  userAgent,
}: {
  diffJson: string;
  userAgent: string | null;
}) {
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(diffJson);
  } catch {
    parsed = null;
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/30 p-3 text-xs">
      {parsed && typeof parsed === "object" ? (
        <DiffTable obj={parsed as Record<string, unknown>} />
      ) : (
        <pre className="overflow-x-auto whitespace-pre-wrap break-all text-muted-foreground">
          {diffJson}
        </pre>
      )}
      {userAgent && (
        <div className="text-[10px] text-muted-foreground">UA: {userAgent}</div>
      )}
    </div>
  );
}

function DiffTable({ obj }: { obj: Record<string, unknown> }) {
  const entries = Object.entries(obj);
  if (entries.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <div className="grid gap-1.5">
      {entries.map(([key, value]) => (
        <DiffEntry key={key} field={key} value={value} />
      ))}
    </div>
  );
}

function DiffEntry({ field, value }: { field: string; value: unknown }) {
  const t = useT("audit");
  const prettyValue = (v: unknown): string => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "string") return v;
    if (typeof v === "boolean") return v ? t("on") : t("off");
    return String(v);
  };
  if (
    value &&
    typeof value === "object" &&
    "oldValue" in value &&
    "newValue" in value
  ) {
    const v = value as { oldValue: unknown; newValue: unknown };
    return (
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="font-medium text-foreground">{field}:</span>
        <span className="text-muted-foreground line-through">
          {prettyValue(v.oldValue)}
        </span>
        <span className="inline-block text-muted-foreground rtl:-scale-x-100">→</span>
        <span className="text-foreground">{prettyValue(v.newValue)}</span>
      </div>
    );
  }
  if (
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    return (
      <div className="space-y-1">
        <span className="font-medium text-foreground">{field}:</span>
        <pre className="overflow-x-auto whitespace-pre-wrap rounded border bg-background p-2 text-[11px] text-muted-foreground">
          {JSON.stringify(value, null, 2)}
        </pre>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-baseline gap-1.5">
      <span className="font-medium text-foreground">{field}:</span>
      <span className="text-foreground">{prettyValue(value)}</span>
    </div>
  );
}

/**
 * EntityType + EntityId → dashboard route. Eşleşmeyenler için null (yalın metin).
 * Bilinen tipler:
 *   Order → /orders/{orderNumber} — ama EntityId Guid (Order.Id), order detail
 *   slug'ı OrderNumber kullanıyor. Diff/audit'te orderNumber field var, link
 *   yerine null bırakıp hash gösteriyoruz; gelecek iyileştirme: backend
 *   audit row'una `entityRoute` eklenir.
 *   Member → /team
 *   Event → /events (bizde event detail slug ile, ama audit Guid taşıyor;
 *   şimdilik /events listesi)
 *   PaymentMethod → /shops (id'de "{shopId}:{kind}" tutuyor ama navigate
 *   için shop slug lazım; null bırak)
 */
function buildEntityHref(row: AuditLogEntry): string | null {
  switch (row.entityType) {
    case "Member":
      return "/team";
    case "Event":
      return "/events";
    case "EventReport":
      return `/reports/events/${row.entityId}`;
    case "Shop":
      return "/shops";
    default:
      return null;
  }
}

function shortId(id: string): string {
  if (id.length <= 8) return id;
  return id.slice(0, 8);
}
